import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import {
  fetchHardposDevices,
  upsertHardposDevice,
  deleteHardposDevice,
  updateHardposDevice,
} from "../api/api";
import { FaTrash } from "react-icons/fa";
import { useLocation } from "react-router-dom";
export default function HardPOS({ isSidebarOpen }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const mode = params.get("mode");

  const isAddMode = mode === "add";
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(isAddMode);
  const [editingRows, setEditingRows] = useState({});

  const [mid, setMid] = useState("");
  const [tid, setTid] = useState("");
  const [serial, setSerial] = useState("");
  const [merchantName, setMerchantName] = useState("");
  const [cnic, setCnic] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  useEffect(() => {
    setShowForm(isAddMode);
  }, [isAddMode]);
  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchHardposDevices();
      setRows(res?.data || []);
    } catch (e) {
      console.error(e);
      setRows([]);
      alert("Failed to fetch Hard POS devices");
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (row) => {
    const id = row?.id;
    if (!id) {
      alert("Missing id");
      return;
    }

    const ok = window.confirm("Delete this HardPOS device?");
    if (!ok) return;

    try {
      const res = await deleteHardposDevice(id);
      if (res?.isSuccess) {
        await load();
      } else {
        alert(res?.message || "Failed to delete device");
      }
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || "Failed to delete device");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!mid.trim() || !tid.trim() || !serial.trim()) {
      alert("MID, TID and Serial No are required");
      return;
    }

    if (!merchantName.trim() || !cnic.trim() || !mobileNo.trim()) {
      alert("Merchant Name, CNIC and Mobile No are required");
      return;
    }

    try {
      setSubmitting(true);
      const res = await upsertHardposDevice(
        mid.trim(),
        tid.trim(),
        serial.trim(),
        merchantName.trim(),
        cnic.trim(),
        mobileNo.trim(),
      );
      if (res?.isSuccess) {
        setMid("");
        setTid("");
        setSerial("");
        setMerchantName("");
        setCnic("");
        setMobileNo("");
        setShowForm(false);
        await load();
        alert(res?.message || "Device saved successfully");
      } else {
        alert(res?.message || "Failed to save device");
      }
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Failed to save device");
    } finally {
      setSubmitting(false);
    }
  };

  const textEditor = (options) => {
    return (
      <InputText
        value={options.value || ""}
        onChange={(e) => options.editorCallback(e.target.value)}
        className="form-input"
        style={{ width: "100%" }}
      />
    );
  };

  const onRowEditComplete = async (e) => {
    const id = e?.newData?.id;
    if (!id) {
      alert("Missing id");
      return;
    }

    try {
      const res = await updateHardposDevice(id, {
        MID: e.newData.MID,
        TID: e.newData.TID,
        serial_number: e.newData.serial_number,
        merchant_name: e.newData.merchant_name,
        cnic: e.newData.cnic,
        mobile_no: e.newData.mobile_no,
      });
      if (!res?.isSuccess) {
        alert(res?.message || "Failed to update device");
      }
      await load();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Failed to update device");
      await load();
    }
  };

  if (loading) {
    return (
      <div className={`reporting-container ${isSidebarOpen ? "shifted" : ""}`}>
        <p className="loader">Loading Hard POS...</p>
      </div>
    );
  }

  return (
    <div className={`reporting-container ${isSidebarOpen ? "shifted" : ""}`}>
      <section className="reporting-table-section">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "14px 18px 0 18px",
          }}
        >
          <h2 style={{ margin: 0 }}>
            {isAddMode ? "Add Hard POS" : "Hard POS"}
          </h2>
          {!isAddMode && (
            <button
              type="button"
              onClick={() => setShowForm((s) => !s)}
              style={{
                border: "1px solid var(--primary)",
                background: "var(--primary)",
                color: "#fff",
                padding: "10px 14px",
                borderRadius: "10px",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              +
            </button>
          )}
        </div>

        {showForm && (
          <form
            onSubmit={onSubmit}
            style={{
              padding: "12px 18px 0 18px",
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(200px, 1fr))",
              gap: "12px",
              alignItems: "end",
            }}
          >
            <div
              style={{ display: "flex", flexDirection: "column", gap: "6px" }}
            >
              <label>MID</label>
              <input
                value={mid}
                onChange={(e) => setMid(e.target.value)}
                className="form-input"
                placeholder="Enter MID"
                maxLength={15}
              />
            </div>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "6px" }}
            >
              <label>TID</label>
              <input
                value={tid}
                onChange={(e) => setTid(e.target.value)}
                className="form-input"
                placeholder="Enter TID"
                maxLength={8}
              />
            </div>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "6px" }}
            >
              <label>Serial No</label>
              <input
                value={serial}
                onChange={(e) => setSerial(e.target.value)}
                className="form-input"
                placeholder="Enter Serial No"
                maxLength={100}
              />
            </div>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "6px" }}
            >
              <label>Merchant Name</label>
              <input
                value={merchantName}
                onChange={(e) => setMerchantName(e.target.value)}
                className="form-input"
                placeholder="Enter Merchant Name"
              />
            </div>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "6px" }}
            >
              <label>CNIC</label>
              <input
                value={cnic}
                onChange={(e) => setCnic(e.target.value)}
                className="form-input"
                placeholder="Enter CNIC"
              />
            </div>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "6px" }}
            >
              <label>Mobile No</label>
              <input
                value={mobileNo}
                onChange={(e) => setMobileNo(e.target.value)}
                className="form-input"
                placeholder="Enter Mobile No"
              />
            </div>

            <div style={{ gridColumn: "1 / -1", display: "flex", gap: "10px" }}>
              <button
                type="submit"
                disabled={submitting}
                style={{
                  border: "1px solid var(--primary)",
                  background: "var(--primary)",
                  color: "#fff",
                  padding: "10px 14px",
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                {submitting ? "Saving..." : "Add Device"}
              </button>

              <button
                type="button"
                onClick={() => {
                  if (isAddMode) {
                    window.history.back();
                  } else {
                    setShowForm(false);
                  }
                }}
                disabled={submitting}
                style={{
                  border: "1px solid var(--primary)",
                  background: "transparent",
                  color: "var(--primary)",
                  padding: "10px 14px",
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <div style={{ padding: "14px 18px 18px 18px" }}>
          <DataTable
            value={rows}
            dataKey="id"
            className="dashboard-table inventory-table"
            responsiveLayout="scroll"
            editMode="row"
            editingRows={editingRows}
            onRowEditChange={(e) => setEditingRows(e.data)}
            onRowEditComplete={onRowEditComplete}
            paginator
            rows={10}
            paginatorClassName="custom-paginator"
          >
            <Column
              field="MID"
              header="MID"
              style={{ width: "180px" }}
              editor={textEditor}
            />
            <Column
              field="TID"
              header="TID"
              style={{ width: "140px" }}
              editor={textEditor}
            />
            <Column
              field="serial_number"
              header="Serial No"
              style={{ width: "220px" }}
              editor={textEditor}
            />
            <Column
              field="merchant_name"
              header="Merchant Name"
              style={{ width: "200px" }}
              editor={textEditor}
            />
            <Column
              field="cnic"
              header="CNIC"
              style={{ width: "180px" }}
              editor={textEditor}
            />
            <Column
              field="mobile_no"
              header="Mobile No"
              style={{ width: "150px" }}
              editor={textEditor}
            />

            <Column
              rowEditor
              header=""
              style={{ width: "44px", textAlign: "center" }}
              bodyStyle={{
                padding: "8px 2px",
                textAlign: "center",
                verticalAlign: "middle",
              }}
            />

            <Column
              header=""
              body={(rowData) => (
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => onDelete(rowData)}
                    style={{
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      padding: 0,
                      color: "#dc2626",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                </div>
              )}
              style={{ width: "44px", textAlign: "center" }}
              bodyStyle={{
                padding: "8px 2px",
                textAlign: "center",
                verticalAlign: "middle",
              }}
            />
          </DataTable>
        </div>
      </section>
    </div>
  );
}
